import { FC, useState } from 'react';
import { useMutation } from '@apollo/client';

import { throwOnlyUnhandledErrors } from 'common/hooks/useErrorHandledMutation';
import { logEvent } from 'features/Analytics/analytics';

import { GetBeneficiariesDataDocument } from '../../../../graphql/queries/getBeneficiariesData.gql';
import { UpdatePhysicalPersonLegalBeneficiaryDocument } from '../../graphql/mutations/updatePhysicalPersonLegalBeneficiary.gql';
import Component from './component';
import {
  CreatePhysicalPersonLegalBeneficiaryDocument,
  DeletePhysicalPersonLegalBeneficiaryDocument,
} from './graphql/mutations.gql';
import { ManageBeneficiaryElementProps } from './types';

const ManageBeneficiaryElement: FC<ManageBeneficiaryElementProps> = ({
  beneficiary,
  companyProfileId,
  disableAccountHolderSwitch,
  getIsAddedAmountSharesValid,
  includeInvitationRole,
  includeShares,
  isExistingCompany,
  onDelete = () => {},
  onDoneEditing = () => {},
  sharesData,
}) => {
  const isNewBeneficiary = !beneficiary?.companyLegalBeneficiaryId;
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(!beneficiary);

  const [createBeneficiaryMutation] = useMutation(
    CreatePhysicalPersonLegalBeneficiaryDocument,
    {
      awaitRefetchQueries: true,
      refetchQueries: [
        {
          query: GetBeneficiariesDataDocument,
          variables: { companyProfileId, includeShares: false },
        },
      ],
    },
  );

  const [updateBeneficiaryMutation] = useMutation(
    UpdatePhysicalPersonLegalBeneficiaryDocument,
    {
      awaitRefetchQueries: true,
      refetchQueries: [
        {
          query: GetBeneficiariesDataDocument,
          variables: { companyProfileId, includeShares: false },
        },
      ],
    },
  );

  const [deleteBeneficiaryMutation] = useMutation(
    DeletePhysicalPersonLegalBeneficiaryDocument,
    {
      awaitRefetchQueries: true,
      refetchQueries: [
        {
          query: GetBeneficiariesDataDocument,
          variables: { companyProfileId, includeShares: false },
        },
      ],
    },
  );

  return (
    <Component
      beneficiary={beneficiary}
      disableAccountHolderSwitch={disableAccountHolderSwitch}
      getIsAddedAmountSharesValid={getIsAddedAmountSharesValid}
      includeInvitationRole={includeInvitationRole}
      includeShares={includeShares}
      isDeleting={isDeleting}
      isEditing={isEditing}
      isExistingCompany={isExistingCompany}
      isSaving={isSaving}
      onCardClicked={() => {
        if (!isEditing) {
          setIsEditing(true);

          logEvent({
            name: 'Onboarding Beneficiary List Edit Pressed',
            usePrefix: false,
          });
        }
      }}
      onClose={() => {
        setIsEditing(false);
        onDoneEditing();
      }}
      onDelete={async () => {
        if (!isNewBeneficiary) {
          setIsDeleting(true);
          try {
            // For beneficiaries not in DB we'll just let the parent remove it from the new list
            await deleteBeneficiaryMutation({
              variables: {
                input: {
                  companyLegalBeneficiaryId:
                    beneficiary.companyLegalBeneficiaryId as string,
                  companyProfileId,
                },
              },
            });
          } catch (error) {
            throwOnlyUnhandledErrors(error);
          }
          setIsDeleting(false);
        }

        logEvent({
          name: 'Onboarding Beneficiary List Person Deleted',
          usePrefix: false,
        });

        onDelete();
        onDoneEditing();
      }}
      onSave={async (editedBeneficiary) => {
        setIsSaving(true);
        try {
          if (!isNewBeneficiary) {
            await updateBeneficiaryMutation({
              variables: {
                input: {
                  companyLegalBeneficiaryId:
                    beneficiary?.companyLegalBeneficiaryId as string,
                  companyProfileId,
                  ...editedBeneficiary,
                },
              },
            });
          } else {
            if (
              !editedBeneficiary.isDirector &&
              !editedBeneficiary.isMajorShareholder
            ) {
              if (!includeShares) {
                throw new Error(
                  "Can't save a legal beneficiary who isn't director or major shareholder",
                );
              }

              if (!editedBeneficiary.numberOfShares) {
                throw new Error(
                  'numberOfShares is required to create a company shareholder',
                );
              }
            }

            await createBeneficiaryMutation({
              variables: {
                input: {
                  companyProfileId,
                  ...editedBeneficiary,
                },
              },
            });
          }

          setIsEditing(false);

          logEvent({
            name: 'Onboarding Beneficiary List Person Saved',
            usePrefix: false,
          });
        } catch (error) {
          throwOnlyUnhandledErrors(error);
        }
        setIsSaving(false);
        onDoneEditing();
      }}
      sharesData={sharesData}
    />
  );
};

export default ManageBeneficiaryElement;
